import { CancelTokenSource } from 'axios'
import { IPatchModel } from 'shared/models/Common'
import { IFilterModel, IListModel } from 'shared/models/List'
import {
  BoardCaseStatusEnum,
  BoardCaseTypeEnum,
  FinishCaseStatusEnum,
  IBoardCaseAttachmentModel,
  IBoardCaseLinks,
  IBoardCasePerson,
} from './BoardCase'

export interface IBoardMeeting {
  id: string
  title: string
  time: Date
  attendantCount: number
  caseCount: number
  status: MeetingStatusEnum
  statusText: string
  canDownloadProtocol: boolean
  sharedWithBbl: boolean
  canSharedWithBblBeChanged: boolean
}

export interface IMeetingEditedByPerson {
  fullName: string
  id: string
}

export interface IBoardDetailMeeting {
  id: string
  title: string
  description: string
  place: string
  from?: Date
  to?: Date
  status: MeetingStatusEnum
  participants: IBoardMeetingParticipant[]
  editingByUser?: IMeetingEditedByPerson | null
}

export interface IBoardMeetingsList extends IListModel {
  result: IBoardMeeting[]
}

export interface IBoardMeetingFilter
  extends Omit<IFilterModel, 'sortcolumn' | 'sortorder'> {
  clientId: string
  sortColumn: MeetingsSortColumnEnum
  sortOrder: string
  queryStatus: MeetingQueryStatusEnum

  to?: Date
  from?: Date
  source?: CancelTokenSource
}

export interface IBoardMeetingPeriod {
  periodEnd: Date
  periodStart: Date
  displayName: string
}

export enum BoardFunctionAsStringEnum {
  BoardLeader = 'Styreleder',
  BoardMember = 'Styremedlem',
  Deputy = 'Nestleder',
  Cashier = 'Kasserer',
  BblRepresentant = 'BBL-representant',
  DigitalKeys = 'Digitale nøkler',
  Accountant = 'Revisor',
  Secretary = 'Sekretær',
  Other = 'Annet',
  DeputyMember = 'Varamedlem',
  External = 'Ekstern',
  Advisor = 'Rådgiver',
}

export enum MeetingsSortColumnEnum {
  Title = 'title',
  Time = 'time',
  AttendantCount = 'attendantCount',
  CaseCount = 'caseCount',
  Status = 'status',
}

export enum MeetingQueryStatusEnum {
  All = 'all',
  Active = 'active',
  ActiveMeetingsThatCanBeConnectedToCases = 'activeMeetingsThatCanBeConnectedToCases',
  Completed = 'completed',
  ActiveUpcoming = 'activeUpcoming',
}

export enum MeetingStatusEnum {
  New = 'New',
  Ongoing = 'Ongoing',
  ConductedNotCompleted = 'ConductedNotCompleted',
  CompletedAndSigned = 'CompletedAndSigned',
  CompletedNotYetSigned = 'CompletedNotYetSigned',
  CompletedManualSigning = 'CompletedManualSigning',
}

export enum BoardMeetingSectionEnum {
  Meetings = 'moeter',
  Cases = 'saker',
  FixedCases = 'fastesaker',
}

export enum BoardMeetingPatchPaths {
  Place = '/place',
  Title = '/title',
  Description = '/description',
  From = '/from',
  To = '/to',
  Participants = '/participants',
}

export enum CompleteMeetingTypeEnum {
  Conduct = 'conduct',
  Complete = 'complete',
  Signed = 'signed',
}

export enum MeetingListActionsEnum {
  DownloadProtocol = 'downloadProtocol',
  DownloadNotice = 'downloadNotice',
  DownloadCalendar = 'downloadCalendar',
  RemoveAccess = 'removeAccess',
  AddAccess = 'addAccess',
  GoToProtocoll = 'goToProtocoll',
}

export interface IBoardMeetingPatchWrapper {
  models: IPatchModel[]
  id: string
}

export interface ICreateBoardMeetingModel {
  title: string
  from: Date
  to: Date | null
}

export enum BoardMeetingProccessingState {
  MeetingCreating = 'MeetingCreating',
  MeetingCreated = 'MeetingCreated',
  MeetingCreatingError = 'MeetingCreatingError',

  MeetingAgendaUpdate = 'MeetingCreating',
  MeetingAgendaUpdated = 'MeetingCreated',

  Deleting = 'Deleting',
  Deleted = 'Deleted',
  DeleteError = 'DeleteError',

  ParticipantsUpdating = 'ParticipantsUpdating',
  ParticipantsUpdated = 'ParticipantsUpdated',
  ParticipantsUpdateError = 'ParticipantsUpdateError',

  MeetingUpdating = 'MeetingUpdating',
  MeetingUpdated = 'MeetingUpdated',
  MeetingUpdateError = 'MeetingUpdateError',

  MeetingStatusUpdating = 'MeetingStatusUpdating',
  MeetingUpdateResponsiblePerson = 'MeetingUpdateResponsiblePerson',
}

export interface IBoardMeetingParticipantForEdit
  extends IBoardMeetingParticipant {
  selected: boolean
  mustBeRemovedBeforeSpecifiedNewTimeOfMeetingCanBeApplied: boolean
}

export interface IBoardMeetingParticipant {
  participantId?: string
  name?: string
  boardFunction?: string
  boardFunctionCustomName?: string
  hasLeftBoardSinceTimeOfMeeting: boolean
  boardContactNumber?: number
  nameregisterId?: string
  canVote: boolean
  canSign: boolean
  meetingChair: boolean
  email?: string
  firstName?: string
  lastName?: string
  newExternalParticipant?: boolean
  emailLastSent?: Date
  hasMet: boolean
  rejectedAt?: Date
  approvedAt?: Date
}

export interface IBoardMeetingParticipantPatchModel {
  nameregisterId?: string
  canVote?: boolean
  meetingChair: boolean
  boardContactNumber?: number
  email?: string
  firstName?: string
  lastName?: string
}

export interface ISelectedParticipantModel {
  id?: string
  name: string
  email?: string
}

export interface IMeetingCaseVotes {
  boardFunction: string
  name: string
  nameId: string
  vote: MeetingCaseVoteEnum
}

export type IMeetingAgendaList = IListModel<IMeetingAgendaModel>

export interface IMeetingAgendaModel {
  agendaId?: string
  created?: Date
  caseId: string
  order: number
  title: string
  decision?: string
  description: string
  type: BoardCaseTypeEnum
  responsible?: IBoardCasePerson
  submittedBy?: string
  deadline?: Date
  isStandardCase: boolean
  status: BoardCaseStatusEnum
  votes: IMeetingCaseVotes[]
  suggestedDecision?: string
  ignoreUntilSaved?: boolean
  votingStatus?: MeetingCaseVoteEnum
  isProcessedInThisMeeting?: boolean
  attachments?: IBoardCaseAttachmentModel[]
  links?: IBoardCaseLinks[]
}

export interface IMeetingAgendaSubmitModel {
  caseId: string
  order: number
}

export interface IMeetingAgendaCollection {
  agenda: IMeetingAgendaModel[]
  proposed: IMeetingAgendaModel[]
  fixed: IMeetingAgendaModel[]
}

export interface INewExternalParticipant {
  boardContactNumber?: number
  firstName: string
  lastName: string
  email: string
}

export interface IMeetingAttendanceParticipant {
  id: string
  name: string
  canVote: boolean
  hasMet: boolean
  meetingChair?: boolean
  boardFunction: string
  boardFunctionCustomName: string
  hasLeftBoardSinceTimeOfMeeting: boolean
  nameId?: string
}

export const enum DecisionChoisesEnum {
  No,
  Yes,
  NotAvailable,
  Undefined,
}

export enum MeetingCaseVoteEnum {
  For = 'For',
  Against = 'Against',
  NotVoted = 'NotVoted',
}

export interface IDecisionVoteRequestModel {
  nameId: string
  vote: DecisionChoisesEnum
}

export interface INewDecisionRequestModel {
  caseId: string
  decision: string
  decisionStatus: FinishCaseStatusEnum
  votes: IDecisionVoteRequestModel[] | null
  suggestedDecision?: string
}

export interface IDecisionCaseHandlingModel {
  decision: string
  plainTextLength: number
  votes: {
    unanimous: DecisionChoisesEnum
    [x: string]: DecisionChoisesEnum
  }
}

export interface IProtocolDetails {
  createdByNameId: string
  createdDate: Date
  id: string
  referanceId: string
  referanceType: string
  status: 'SentToPortal' | 'SentToAll'
}

export interface ISignatories {
  signedByNameId: string
  signedDate: Date
  signedByFullName: string
}

export interface ISignatorySimplified {
  nameId: string
  boardFunction: string
  name: string
  signed: Date | null
}

export interface ISignMeetingDetails {
  title: string
  updatedAt: Date
  status: MeetingStatusEnum
  userThatCanCancelByNameId: IMeetingEditedByPerson
  signatories: ISignatorySimplified[]
}

export interface IAutoSavePayload {
  caseId: string
  text: string
  manualUpdate?: boolean
}
